import { CompanySerivces } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { DisclosureStatementCompanyBuyerOptions } from '@constants_iqloud';

import { CompanyMembershipActionLabels, CompanyMembershipStatus } from '@constants_common';
import {
  CompanyProfileDataBodyKeyLiteralList,
  CompanyProfileDataTitleKeyLiteralList,
  CompanyStep,
  ProfileImageStatusType,
  CompanyType,
  CommissionRateTypeKey,
} from '@constants_iqfu';

export type CompanyUsersType = {
  id: number;
  name: string;
  email: string;
  destroyable: boolean;
  notification_required: boolean;
};

export type CreateEditCompanyUserType = {
  name: string;
  email: string;
  password?: string;
};

export type SearchCompanyQuery = {
  name: string;
  address: string | null;
  step: '' | 'created' | 'preparing' | 'inputting' | 'completed';
  is_public: '' | 'true' | 'false';
  unapproved: boolean;
  rejected?: boolean;
  offset: number;
  limit: number;
};

export type SearchAdminCompanyQuery = SearchCompanyQuery & {
  company_type: '' | 'general_agency' | 'purchase_agency';
  company_service_pro: '' | 'true' | 'false';
  iqfu_start_date_from: string;
  iqfu_start_date_to: string;
};

export type CreateCompanyType = {
  name: string;
  display_name?: string;
  town_code: string;
  address: string;
  zipcode: string;
  url: string | null;
  lat: number | undefined;
  long: number | undefined;
  tel: string;
  fax: string | null;
  email: string;
  commission_rate_type_key: keyof typeof CommissionRateTypeKey;
  hearing_sheet: boolean;
};

export type CompanyDetailType = {
  id: number;
  company_type: keyof typeof CompanyType;
  name: string;
  prefecture: { code: number; name: string };
  city: { code: number; name: string };
  town: { code: string; name: string };
  address: string;
  zipcode: string;
  full_address: string;
  lat: number;
  long: number;
  tel: string;
  fax: string;
  url: string;
  admin_cloud_map_note: string;
  email: string;
  display_name: string;
  is_public: boolean;
  step: keyof typeof CompanyStep;
  license_location_number: number;
  license_renewal_count: number;
  license_number: string;
  destroyable: boolean;
  strong_point: 'sell_high' | 'sell_fast' | 'not_decline';
  disclosure_statement_company_buyer: keyof typeof DisclosureStatementCompanyBuyerOptions;
  chat_inquiry_room_id: number;
  company_tags: TagType[];
  all_tags: TagType[];
  iqfu: { has_iqfu: boolean; start_date: string; total_count: number; recent_count: number };
  cloud: { has_cloud: boolean; start_date: string; total_count: number; recent_count: number };
  free_map_code: string;
  purchase_availability: boolean;
  company_services: CompanySerivces[];
  commission_rate_type_key: keyof typeof CommissionRateTypeKey;
  membership_status: CompanyMembershipStatusType;
  company_representative_name: string;
  company_representative_contact: string;
  reciprocal_link_url: string;
  reciprocal_link_position: string;
  publish_notification_sendable: boolean;
  initial_auto_message?: {
    content: string;
    preview_variables: {
      seller_name: string;
      regular_closing_day: string;
      business_hours: string;
    };
  };
};

export type CompanyTagsResponseType = {
  company_tags: TagType[];
};

export type CompanyTagsType = {
  high_priority: number[];
  low_priority: number[];
};

export type UpdateCompanyInformationType = {
  name: string;
  town_code: string;
  address: string;
  zipcode: string;
  lat: number;
  long: number;
  tel: string;
  fax?: string;
  url?: string;
  email: string;
  display_name?: string;
  is_public: boolean;
  license_location_number?: number;
  license_renewal_count?: number;
  license_number?: string;
  strong_point?: 'sell_high' | 'sell_fast' | 'not_decline';
  company_tags?: number[];
  company_representative_name?: string;
  company_representative_contact?: string;
  reciprocal_link_url?: string;
  reciprocal_link_position?: string;
};

export type CompanyProfileDataType = {
  id: number;
  top_title: string;
  files: CompanyProfileImageType[];
  has_profile: boolean;
  has_rejected_request: boolean;
  has_unapproved_update_request: boolean;
  has_profile_update_request: boolean;
  ai_profile_summary: string;
} & {
  [key in CompanyProfileDataTitleKeyLiteralList]: string;
} & {
  [key in CompanyProfileDataBodyKeyLiteralList]: string;
};

export type CompanyProfileImageType = {
  id: number;
  file: string;
  is_main: boolean;
  destroyable: boolean;
  rejected_reason: string;
  status: keyof typeof ProfileImageStatusType;
  sort_key: number;
};

export type CompanyOverViewType = {
  id: number;
  content: string;
  is_required: boolean;
  name: string;
  placeholder: string;
};

export type ProfileItemsType = {
  top_title: string;
  has_profile_update_request?: boolean;
  has_unapproved_update_request?: boolean;
  has_rejected_request?: boolean;
  rejected_reason?: string;
} & {
  [key in CompanyProfileDataTitleKeyLiteralList]: string;
} & {
  [key in CompanyProfileDataBodyKeyLiteralList]: string;
};

export type TagType = {
  description: string;
  id: number;
  name: string;
};

export type CompanyMembershipStatusType = keyof typeof CompanyMembershipStatus;
export type CompanyMembershipActionType = keyof typeof CompanyMembershipActionLabels;

export type DealHistoryPage = {
  id?: number;
  page_name: DealHistoryPageName;
  url: string;
};

export const DealHistoryPageNameLabel = {
  suumo: 'SUUMO',
  at_home: 'at home',
  homes: "HOME'S",
  own_site: '自社サイト',
  other: 'その他',
} as const;

export type DealHistoryPageName = keyof typeof DealHistoryPageNameLabel;

export const MAX_DEAL_HISTORY_PAGES = 10;

export type CompanyMembershipActionButton = {
  label: typeof CompanyMembershipActionLabels[keyof typeof CompanyMembershipActionLabels];
  action: CompanyMembershipActionType;
};

export const CompanyMembershipStatusToAvailableActionsMap: Record<
  CompanyMembershipStatusType,
  CompanyMembershipActionButton[]
> = {
  active: [
    { label: CompanyMembershipActionLabels.suspend, action: 'suspend' },
    { label: CompanyMembershipActionLabels.pre_discard, action: 'pre_discard' },
    { label: CompanyMembershipActionLabels.discard, action: 'discard' },
  ],
  suspended: [
    { label: CompanyMembershipActionLabels.reactivate, action: 'reactivate' },
    { label: CompanyMembershipActionLabels.pre_discard, action: 'pre_discard' },
    { label: CompanyMembershipActionLabels.discard, action: 'discard' },
  ],
  pre_discarded: [{ label: CompanyMembershipActionLabels.discard, action: 'discard' }],
  discarded: [],
};
